<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Solicitudes de vacaciones</h1>
    </div>
    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">
          <div class="w-full w-md-auto md-flex-1">
            <div class="d-flex justify-content-between align-items-center justify-content-lg-start">
              <button
                type="button"
                class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters"
              >
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>

              <div class="d-md-none">
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold m-0"
                  v-b-tooltip.hover
                  title="Recargar"
                  @click="loadData()"
                >
                  <i class="ri-loop-right-line"></i>
                </button>
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold mr-0"
                  v-b-tooltip.hover
                  title="Descargar"
                  @click="downloadReport()"
                >
                  <i class="ri-download-2-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0">
            <div class="d-none d-md-flex">
              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold"
                v-b-tooltip.hover
                title="Recargar"
                @click="loadData()"
              >
                <i class="ri-loop-right-line"></i>
              </button>
              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold ml-0"
                v-b-tooltip.hover
                title="Descargar"
                @click="downloadReport()"
              >
                <i class="ri-download-2-line"></i>
              </button>
            </div>

            <div class="w-full w-md-auto md-flex-1 d-flex flex-column flex-md-row">
              <SearchBar
                placeholder="Buscar por nombre o número de documento"
                @search="onSearch"
                @input="search = $event"
                class="flex-1"
              />

              <div class="text-right mt-2 mt-md-0">
                <b-button
                  :to="{ name: 'vacation-requests-create' }"
                  variant="none"
                  class="button button-primary ml-md-2"
                >Agregar</b-button>
              </div>
            </div>
          </section>

          <filter-menu
            :show="showFilters"
            @hide="showFilters = false"
            :options="filterOptions"
            :selectedOptionFilter="filters"
            @setFilters="setFilters"
          ></filter-menu>
        </div>

        <div
          class="w-100 d-flex flex-wrap align-items-center mt-2"
          v-if="showFiltersList"
        >
          <span
            v-for="(val, name) in filterList"
            v-bind:key="name"
            class="filter-item"
          >
            {{ getFilterName(name, val) }}
            <button
              type="button"
              class="text-white p-0 bg-transparent border-0 ml-2"
              @click="deleteFilter(name)"
            >
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <!-- Tabla desktop -->
      <div class="d-none d-md-block">
        <table class="custom-table">
          <thead>
            <tr>
              <th>COLABORADOR</th>
              <th>EMPRESA</th>
              <th>ÁREA</th>
              <th>PERÍODO</th>
              <th>FECHA INICIO</th>
              <th>FECHA FIN</th>
              <th>DÍAS</th>
              <th>ESTADO</th>
              <th class="pr-md-3">OPCIONES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="list.length === 0">
              <td colspan="9" class="text-center text-muted py-4">No se encontraron solicitudes</td>
            </tr>
            <tr v-for="item in list" :key="item.id">
              <td>{{ item.employeeName }}</td>
              <td>{{ item.companyName }}</td>
              <td>{{ item.areaName }}</td>
              <td>{{ item.startDate | date }}</td>
              <td>{{ item.startDate | date }}</td>
              <td>{{ item.endDate | date }}</td>
              <td>{{ item.calendarDays }}</td>
              <td>
                <span :class="['badge', getStatusColor(item.status)]">{{ item.status }}</span>
              </td>
              <td class="cell-center d_option pr-md-2 position-relative">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem
                      text="Ver detalle"
                      :to="{ name: 'vacation-requests-detail', params: { id: item.id } }"
                    />
                    <DropdownItem
                      v-if="item.status === 'PENDIENTE'"
                      text="Aprobar"
                      @click="openApprove(item)"
                    />
                    <DropdownItem
                      v-if="item.status === 'PENDIENTE'"
                      text="Rechazar"
                      @click="openReject(item)"
                    />
                    <DropdownItem
                      v-if="item.status === 'APROBADA'"
                      text="Cancelar"
                      @click="openCancel(item)"
                    />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tabla mobile -->
      <div class="d-md-none">
        <div v-if="list.length === 0" class="text-center text-muted py-4">No se encontraron solicitudes</div>
        <div v-for="item in list" :key="item.id" class="mobile-card mb-3 p-3 border rounded">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <strong>{{ item.employeeName }}</strong>
              <div class="text-muted small">{{ item.companyName }} · {{ item.areaName }}</div>
            </div>
            <span :class="['badge', getStatusColor(item.status)]">{{ item.status }}</span>
          </div>
          <div class="mt-2 small">
            <span>{{ item.startDate | date }} — {{ item.endDate | date }}</span>
            <span class="ml-2">({{ item.calendarDays }} días)</span>
          </div>
          <div class="mt-2 d-flex">
            <router-link :to="{ name: 'vacation-requests-detail', params: { id: item.id } }" class="button button-light button-sm mr-2">
              Ver
            </router-link>
            <button v-if="item.status === 'PENDIENTE'" type="button" class="button button-primary button-sm mr-2" @click="openApprove(item)">Aprobar</button>
            <button v-if="item.status === 'PENDIENTE'" type="button" class="button button-danger button-sm mr-2" @click="openReject(item)">Rechazar</button>
            <button v-if="item.status === 'APROBADA'" type="button" class="button button-light button-sm" @click="openCancel(item)">Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Aprobar -->
    <b-modal
      id="modal-approve"
      centered
      no-close-on-esc
      no-close-on-backdrop
      hide-footer
      hide-header
      content-class="confirmation-modal"
    >
      <div class="d-flex justify-content-between">
        <div class="confirmation-modal-title">Aprobar solicitud</div>
        <b-button class="btn-close text-dark" @click="$bvModal.hide('modal-approve')" aria-label="Close">
          <i class="ri-close-large-fill"></i>
        </b-button>
      </div>
      <div class="mt-4 text-center">
        <div class="d-flex justify-content-center align-items-center confirmation-modal-text">
          <span class="warning-icon"><i class="ri-error-warning-line"></i></span>
          <span class="d-inline-block ml-2">
            ¿Confirma que desea aprobar esta solicitud?
            <template v-if="selectedItem">
              <br><strong>{{ selectedItem.employeeName }}</strong> —
              {{ selectedItem.startDate | date }} al {{ selectedItem.endDate | date }}
              ({{ selectedItem.calendarDays }} días)
            </template>
          </span>
        </div>
        <div class="confirmation-modal-action-buttons d-flex justify-content-end align-items-center">
          <button type="button" class="button button-light mr-2" @click="$bvModal.hide('modal-approve')">Cancelar</button>
          <button type="button" class="button button-primary" @click="confirmApprove">Aprobar</button>
        </div>
      </div>
    </b-modal>

    <!-- Modal: Rechazar -->
    <b-modal
      id="modal-reject"
      centered
      no-close-on-esc
      no-close-on-backdrop
      hide-footer
      hide-header
      content-class="confirmation-modal"
    >
      <div class="d-flex justify-content-between">
        <div class="confirmation-modal-title">Rechazar solicitud</div>
        <b-button class="btn-close text-dark" @click="$bvModal.hide('modal-reject')" aria-label="Close">
          <i class="ri-close-large-fill"></i>
        </b-button>
      </div>
      <div class="mt-4 text-center">
        <div class="d-flex justify-content-center align-items-center confirmation-modal-text">
          <span class="warning-icon"><i class="ri-error-warning-line"></i></span>
          <span class="d-inline-block ml-2">¿Confirma que desea rechazar esta solicitud?</span>
        </div>
        <div class="form-group mt-3 text-left">
          <label>Motivo de rechazo <span class="text-danger">*</span></label>
          <b-form-textarea v-model="rejectReason" rows="3" placeholder="Ingrese el motivo del rechazo" />
        </div>
        <div class="confirmation-modal-action-buttons d-flex justify-content-end align-items-center">
          <button type="button" class="button button-light mr-2" @click="$bvModal.hide('modal-reject')">Cancelar</button>
          <button type="button" class="button button-delete" :disabled="!rejectReason.trim()" @click="confirmReject">Rechazar</button>
        </div>
      </div>
    </b-modal>

    <!-- Modal: Cancelar -->
    <b-modal
      id="modal-cancel"
      centered
      no-close-on-esc
      no-close-on-backdrop
      hide-footer
      hide-header
      content-class="confirmation-modal"
    >
      <div class="d-flex justify-content-between">
        <div class="confirmation-modal-title">Cancelar solicitud</div>
        <b-button class="btn-close text-dark" @click="$bvModal.hide('modal-cancel')" aria-label="Close">
          <i class="ri-close-large-fill"></i>
        </b-button>
      </div>
      <div class="mt-4 text-center">
        <div class="d-flex justify-content-center align-items-center confirmation-modal-text">
          <span class="warning-icon"><i class="ri-error-warning-line"></i></span>
          <span class="d-inline-block ml-2">¿Confirma que desea cancelar esta solicitud?</span>
        </div>
        <div class="form-group mt-3 text-left">
          <label>Motivo de cancelación <span class="text-danger">*</span></label>
          <b-form-textarea v-model="cancelReason" rows="3" placeholder="Ingrese el motivo de la cancelación" />
        </div>
        <div class="confirmation-modal-action-buttons d-flex justify-content-end align-items-center">
          <button type="button" class="button button-light mr-2" @click="$bvModal.hide('modal-cancel')">Volver</button>
          <button type="button" class="button button-delete" :disabled="!cancelReason.trim()" @click="confirmCancel">Cancelar solicitud</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  Constants,
  Alert,
  FilterMenu,
  FilterMixin,
  DropdownTable,
  DropdownItem,
  SearchBar,
} from 'wize-admin'
import { VacationService, CompanyService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

export default {
  name: 'VacationRequestList',
  components: { FilterMenu, DropdownTable, DropdownItem, SearchBar },
  mixins: [FilterMixin],
  data() {
    return {
      list: [],
      search: '',
      showFilters: false,
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: Constants.TypeFilters.SINGLE,
          options: Object.values(LocalConstants.VacationRequestStatus).map(s => ({ key: s.key, label: s.label })),
        },
        {
          label: 'Empresa',
          key: 'companyId',
          type: Constants.TypeFilters.SINGLE,
          options: [],
        },
      ],
      selectedItem: null,
      rejectReason: '',
      cancelReason: '',
    }
  },
  methods: {
    onSearch(val) {
      this.search = val
      this.loadData()
    },
    buildFilterParams() {
      const params = {}
      if (this.search)         params.search    = this.search
      if (this.filters.status)    params.status    = this.filters.status
      if (this.filters.companyId) params.companyId = this.filters.companyId
      return params
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.getRequests(this.buildFilterParams())
        this.list = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar solicitudes')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReport() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.downloadXlsx(this.buildFilterParams())
        const today = new Date().toISOString().split('T')[0]
        const url = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `solicitudes-vacaciones-${today}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el reporte')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    getStatusColor(status) {
      const found = Object.values(LocalConstants.VacationRequestStatus).find(s => s.key === status)
      return found ? found.color : 'badge-gray'
    },
    openApprove(item) {
      this.selectedItem = item
      this.$bvModal.show('modal-approve')
    },
    openReject(item) {
      this.selectedItem = item
      this.rejectReason = ''
      this.$bvModal.show('modal-reject')
    },
    openCancel(item) {
      this.selectedItem = item
      this.cancelReason = ''
      this.$bvModal.show('modal-cancel')
    },
    async confirmApprove() {
      this.$bvModal.hide('modal-approve')
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.approveRequest(this.selectedItem.id)
        Alert.success('Solicitud aprobada correctamente')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al aprobar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmReject() {
      this.$bvModal.hide('modal-reject')
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.rejectRequest(this.selectedItem.id, { rejectionReason: this.rejectReason })
        Alert.success('Solicitud rechazada')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al rechazar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmCancel() {
      this.$bvModal.hide('modal-cancel')
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.cancelRequest(this.selectedItem.id, { cancellationReason: this.cancelReason })
        Alert.success('Solicitud cancelada')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cancelar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    try {
      const resp = await CompanyService.listAll()
      this.filterOptions[1].options = (resp.payload || []).map(c => ({ key: c.id, label: c.name }))
    } catch (e) {
      console.error(e)
    }
    this.loadData()
  },
  filters: {
    date(value) {
      if (!value) return '-'
      return value
    },
  },
}
</script>
